import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const refreshTimer = useRef(null);
  const isRefreshing = useRef(false);

  const scheduleRefresh = useCallback((accessToken) => {
    if (refreshTimer.current) clearTimeout(refreshTimer.current);
    // Refresh 5 minutes before expiration (token lasts 60min)
    refreshTimer.current = setTimeout(async () => {
      // Prevent concurrent refresh across tabs
      if (isRefreshing.current) return;
      isRefreshing.current = true;

      const refreshToken = localStorage.getItem('refresh_token');
      if (!refreshToken) { isRefreshing.current = false; return; }
      try {
        const response = await axios.post(`${API}/auth/refresh`, { refresh_token: refreshToken });
        const newToken = response.data.access_token;
        localStorage.setItem('token', newToken);
        setToken(newToken);
        scheduleRefresh(newToken);
      } catch {
        // Refresh failed — force logout
        localStorage.removeItem('token');
        localStorage.removeItem('refresh_token');
        setToken(null);
        setUser(null);
      } finally {
        isRefreshing.current = false;
      }
    }, 55 * 60 * 1000); // 55 minutes
  }, []);

  // Sync token across tabs via storage events
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'token') {
        if (e.newValue) {
          // Another tab refreshed the token — adopt it and reschedule
          setToken(e.newValue);
          scheduleRefresh(e.newValue);
        } else {
          // Another tab logged out
          if (refreshTimer.current) clearTimeout(refreshTimer.current);
          setToken(null);
          setUser(null);
        }
      }
      if (e.key === 'refresh_token' && !e.newValue) {
        // Another tab cleared the refresh token (logout)
        if (refreshTimer.current) clearTimeout(refreshTimer.current);
        setToken(null);
        setUser(null);
      }
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [scheduleRefresh]);

  useEffect(() => {
    const initAuth = async () => {
      const savedToken = localStorage.getItem('token');
      if (savedToken) {
        try {
          const response = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${savedToken}` }
          });
          setUser(response.data);
          setToken(savedToken);
          scheduleRefresh(savedToken);
        } catch {
          // Access token expired — try refresh
          const refreshToken = localStorage.getItem('refresh_token');
          if (refreshToken) {
            try {
              const refreshRes = await axios.post(`${API}/auth/refresh`, { refresh_token: refreshToken });
              const newToken = refreshRes.data.access_token;
              localStorage.setItem('token', newToken);
              setToken(newToken);
              const meRes = await axios.get(`${API}/auth/me`, {
                headers: { Authorization: `Bearer ${newToken}` }
              });
              setUser(meRes.data);
              scheduleRefresh(newToken);
            } catch {
              localStorage.removeItem('token');
              localStorage.removeItem('refresh_token');
              setToken(null);
              setUser(null);
            }
          } else {
            localStorage.removeItem('token');
            setToken(null);
            setUser(null);
          }
        }
      }
      setLoading(false);
    };
    initAuth();
    return () => { if (refreshTimer.current) clearTimeout(refreshTimer.current); };
  }, [scheduleRefresh]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { access_token, refresh_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    if (refresh_token) localStorage.setItem('refresh_token', refresh_token);
    setToken(access_token);
    setUser(userData);
    scheduleRefresh(access_token);
    return userData;
  };

  const register = async (name, email, password, role = 'agent') => {
    const response = await axios.post(`${API}/auth/register`, { name, email, password, role });
    const { access_token, refresh_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    if (refresh_token) localStorage.setItem('refresh_token', refresh_token);
    setToken(access_token);
    setUser(userData);
    scheduleRefresh(access_token);
    return userData;
  };

  const logout = () => {
    if (refreshTimer.current) clearTimeout(refreshTimer.current);
    localStorage.removeItem('token');
    localStorage.removeItem('refresh_token');
    setToken(null);
    setUser(null);
  };

  const value = {
    user,
    token,
    loading,
    login,
    register,
    logout,
    isAuthenticated: !!token && !!user
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Axios instance with auth
export const createAuthAxios = (token) => {
  const instance = axios.create({
    baseURL: API,
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    timeout: 60000, // 60s timeout for AI operations
  });

  // Enrich error objects with backend detail message
  instance.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error.response?.data?.detail) {
        error.userMessage = error.response.data.detail;
      } else if (error.response?.data?.message) {
        error.userMessage = error.response.data.message;
      } else if (error.code === 'ECONNABORTED') {
        error.userMessage = "La requête a expiré. L'opération prend plus de temps que prévu.";
      } else if (!error.response) {
        error.userMessage = "Impossible de contacter le serveur. Vérifiez votre connexion.";
      }
      return Promise.reject(error);
    }
  );

  return instance;
};

export default AuthContext;
